import { AddressesService } from './../../shared/services/addresses.service';
import { Address } from './../../shared/models/classes/address';
import { Component, OnInit, ViewChild } from '@angular/core';
import { trigger, state, style } from '@angular/animations';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';

import { DatatableBaseComponent } from '@components/datatable-base.component';
import { UiService } from '@services/ui.service';
import { MatPaginator } from '@angular/material/paginator';

type LinkedAddressSite = {
  name: string;
  type: 'site' | 'addressGroup';
};

@Component({
  selector: 'app-addresses',
  templateUrl: './addresses.component.html',
  styleUrls: ['./addresses.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*' }))
    ])
  ]
})
export class AddressesComponent extends DatatableBaseComponent implements OnInit {
  addresses: Address[];
  readonly maxVisibleSites = 3;
  private expandedSiteAddressIds: string[] = [];
  private expandedServiceAddressIds: string[] = [];
  allListsExpanded = false;

  displayedColumns = ['name', 'code', 'street', 'streetNumber', 'locality', 'postalCode', 'phone', 'assignement', 'sites', 'tags', 'services', 'actions'];
  dataSource: MatTableDataSource<Address>;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    protected uiSrv: UiService,
    private route: ActivatedRoute,
    private addressesSrv: AddressesService
  ) {
    super(uiSrv);
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  ngOnInit() {
    super.ngOnInit();
    this.getRouteData();
    this.initSubscriptions();
  }

  private initTable(): void {
    this.dataSource = new MatTableDataSource(this.addresses);
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'sites') {
        return this.getLinkedSites(item).map(site => site.name).join(', ');
      }
      return (item as any)[property];
    };

    this.dataSource.filterPredicate = (data, filter: string) => {
      const dataStr = [
        data.name,
        data.code,
        data.street,
        data.streetNumber,
        data.locality,
        data.postalCode,
        data.phone,
        ...this.getLinkedSites(data).map(site => site.name),
      ].join(' ').toLowerCase();
      return dataStr.includes(filter.trim().toLowerCase());
    };

    this.dataSource.sort = this.sort;
  }

  private getRouteData(): void {
    this.route.data
      .subscribe((data: { addresses: Address[] }) => {
        this.addresses = data.addresses;
        this.initTable();
      });
  }

  private initSubscriptions(): void {
    this.addSubscription(
      this.addressesSrv.addressesListChanged
        .subscribe(addresses => {
          this.dataSource.data = addresses || [];
          this.dataSource.sort = this.sort;
          this.onEditFinished();
        })
    );
  }

  delete(idAddress: string) {
    const confirmation = confirm('Êtes-vous certain de vouloir supprimer cette adresse ?');

    if (confirmation) {
      this.addressesSrv.delete(idAddress).subscribe(() => {
        this.uiSrv.showSnackbar('repository.addresses.deleted', true);
      });
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  getVisibleSites(address: Address): LinkedAddressSite[] {
    const linkedSites = this.getLinkedSites(address);

    if (this.isSitesExpanded(address)) {
      return linkedSites;
    }

    return linkedSites.slice(0, this.maxVisibleSites);
  }

  isSitesExpanded(address: Address): boolean {
    return this.expandedSiteAddressIds.includes(address.id);
  }

  toggleSites(address: Address): void {
    const index = this.expandedSiteAddressIds.indexOf(address.id);

    if (index === -1) {
      this.expandedSiteAddressIds.push(address.id);
    } else {
      this.expandedSiteAddressIds.splice(index, 1);
    }
  }

  getVisibleServices(address: Address) {
    const services = address.services || [];

    if (this.isServicesExpanded(address)) {
      return services;
    }

    return services.slice(0, this.maxVisibleSites);
  }

  isServicesExpanded(address: Address): boolean {
    return this.expandedServiceAddressIds.includes(address.id);
  }

  toggleServices(address: Address): void {
    const index = this.expandedServiceAddressIds.indexOf(address.id);

    if (index === -1) {
      this.expandedServiceAddressIds.push(address.id);
    } else {
      this.expandedServiceAddressIds.splice(index, 1);
    }
  }

  toggleAllLists(expanded: boolean): void {
    this.allListsExpanded = expanded;
    const addresses = this.addresses || [];
    const addressIds = addresses.map(address => address.id);

    this.expandedSiteAddressIds = expanded ? [...addressIds] : [];
    this.expandedServiceAddressIds = expanded ? [...addressIds] : [];
  }

  getRemainingSitesCount(address: Address): number {
    return Math.max(this.getLinkedSites(address).length - this.maxVisibleSites, 0);
  }

  private getLinkedSites(address: Address): LinkedAddressSite[] {
    return [
      ...(address.sites || []).map(site => ({ name: site.name, type: 'site' as const })),
      ...(address.addressGroups || []).map(group => ({
        name: group.name,
        type: 'addressGroup' as const,
      })),
    ];
  }
}
